import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
} from '@mui/material';

import { EditButtons } from '@barboxx/admin-components';
import { BRAND_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Brand } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    brand: Brand.DetailsResponse;
}

export const BrandDetailsCard = ({ brand }: Props) => {
    const { products, ...rest } = brand;
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const formContext = useForm<Brand.UpdateRequest>({
        resolver: yupResolver(BRAND_SCHEMA),
        defaultValues: rest,
    });

    const mutateUser = useMutation({
        mutationKey: ['brand', brand.id],
        mutationFn: (data: Brand.UpdateRequest) =>
            Api.Products.Brands.putBrand(brand.id, data),
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: Brand) => {
        return mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated brand!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating brand!', {
                    severity: 'error',
                });
            });
    };

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Card>
                <CardHeader title="Brand Details" />
                <CardContent>
                    <Stack gap={2}>
                        <TextFieldElement
                            fullWidth
                            name="name"
                            disabled={!editable}
                            label="Name"
                        />
                        <TextFieldElement
                            fullWidth
                            name="description"
                            disabled={!editable}
                            label="Description"
                        />
                        <TextFieldElement
                            label="Website"
                            name="website"
                            disabled={!editable}
                            fullWidth
                        />
                        <TextFieldElement
                            label="Manufacturer"
                            name="manufacturer"
                            disabled={!editable}
                            fullWidth
                        />
                        <TextFieldElement
                            label="Country of Origin"
                            name="countryOfOrigin"
                            disabled={!editable}
                            fullWidth
                        />
                    </Stack>
                </CardContent>
                <CardActions
                    sx={{
                        justifyContent: 'flex-end',
                        paddingRight: 3,
                        paddingBottom: 3,
                    }}
                >
                    <Can I="update" a="Brand">
                        <EditButtons
                            formContext={formContext}
                            handleCancel={handleCancel}
                            editable={editable}
                            editableActions={editableActions}
                        />
                    </Can>
                </CardActions>
            </Card>
        </FormContainer>
    );
};
